"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Plus, Check, X, Search } from "lucide-react";
import { useCompareSelection } from "@/hooks/use-compare-selection";
import { API_BASE } from "@/lib/api";
import { getCompareColor, getDisplayName, getId, getImage, getPrice } from "@/lib/comparison";
import { cn, toSlug } from "@/lib/utils";
import type { LaptopInformation } from "@/lib/types";
import type { SeriesConfig, SeriesDetailResponse, SeriesListResponse, SeriesSummary } from "@/lib/catalogue-types";

// The "choose laptops" half of /compare, for visitors who arrive from the nav
// rather than from their results. Search runs client-side over the whole
// series list, same as /laptops: ~265 series is small enough that a query per
// keystroke would only add latency. Opening a series loads its configurations,
// and adding one fetches the full laptop from the public /spec endpoint so the
// comparison has every spec, port and price it renders.

const COUNTRIES = [{
  value: "gb",
  label: "UK",
  symbol: "£"
}, {
  value: "us",
  label: "US",
  symbol: "$"
}];
const RESULT_LIMIT = 12;
function symbolFor(country: string) {
  return COUNTRIES.find(c => c.value === country)?.symbol ?? "£";
}
function formatPrice(sym: string, n: number | null) {
  return n != null && n > 0 ? `${sym}${Math.round(n).toLocaleString()}` : null;
}
function formatStorage(gb: number | null) {
  if (!gb) return null;
  return gb >= 1000 ? `${gb / 1000}TB` : `${gb}GB`;
}
function configKey(slug: string, configNumber: number) {
  return `${slug}/${configNumber}`;
}
function itemKey(it: LaptopInformation) {
  return configKey(toSlug(it.laptop.series), it.laptop.config_number);
}
function matches(series: SeriesSummary, tokens: string[]) {
  const haystack = `${series.series} ${series.company}`.toLowerCase();
  return tokens.every(t => haystack.includes(t));
}

// ── One configuration row inside an opened series ───────────────────────────

function ConfigRow(t0) {
  const $ = _c(39);
  const {
    config,
    sym,
    selected,
    disabled,
    busy,
    onAdd
  } = t0;
  const t1 = config.ram_gb ? `${config.ram_gb}GB RAM` : null;
  let t2;
  if ($[0] !== config.storage_gb) {
    t2 = formatStorage(config.storage_gb) ? `${formatStorage(config.storage_gb)} storage` : null;
    $[0] = config.storage_gb;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = config.display_inches ? `${config.display_inches}"` : null;
  let t4;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== t3) {
    t4 = [t1, t2, t3].filter(Boolean);
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const memory = t4;
  let t5;
  if ($[6] !== config.price || $[7] !== sym) {
    t5 = formatPrice(sym, config.price);
    $[6] = config.price;
    $[7] = sym;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const price = t5;
  let t6;
  if ($[9] !== config.cpu_name) {
    t6 = config.cpu_name && <span className="font-medium text-muted-foreground">, {config.cpu_name}</span>;
    $[9] = config.cpu_name;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== config.config_number || $[12] !== t6) {
    t7 = <p className="truncate text-[13px] font-semibold text-foreground">Model {config.config_number}{t6}</p>;
    $[11] = config.config_number;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = memory.join(", ");
  let t9;
  if ($[14] !== config.gpu_name) {
    t9 = config.gpu_name && <>, {config.gpu_name}</>;
    $[14] = config.gpu_name;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = <p className="mt-0.5 truncate text-xs text-muted-foreground">{t8}{t9}</p>;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t7) {
    t11 = <div className="min-w-0 flex-1">{t7}{t10}</div>;
    $[19] = t10;
    $[20] = t7;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== price) {
    t12 = <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{price}</span>;
    $[22] = price;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = busy || disabled && !selected;
  const t14 = selected ? "bg-brand text-brand-foreground" : "bg-card text-foreground ring-1 ring-border/70 hover:ring-brand";
  let t15;
  if ($[24] !== t14) {
    t15 = cn("inline-flex h-8 w-[5.5rem] shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full text-xs font-semibold transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40", t14);
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== busy || $[27] !== selected) {
    t16 = busy ? "Adding\u2026" : selected ? <><Check className="size-3.5" /> Added</> : <><Plus className="size-3.5" /> Add</>;
    $[26] = busy;
    $[27] = selected;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== onAdd || $[30] !== selected || $[31] !== t13 || $[32] !== t15 || $[33] !== t16) {
    t17 = <button type="button" onClick={onAdd} disabled={t13} aria-pressed={selected} className={t15}>{t16}</button>;
    $[29] = onAdd;
    $[30] = selected;
    $[31] = t13;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t17) {
    t18 = <li className="flex items-center gap-3 px-4 py-3 sm:px-5">{t11}{t12}{t17}</li>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18;
}

// ── Picker ───────────────────────────────────────────────────────────────────

export function ComparePicker({
  onContinue,
  continueLabel
}: {
  onContinue: () => void;
  /** What the main button says once two or more laptops are picked. */
  continueLabel: string;
}) {
  const selection = useCompareSelection();
  const [country, setCountry] = useState<string | null>(null);
  // Cached per market so flipping UK/US back and forth doesn't refetch.
  const [seriesByMarket, setSeriesByMarket] = useState<Record<string, SeriesSummary[] | "error">>({});
  const [query, setQuery] = useState("");
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [configs, setConfigs] = useState<Record<string, SeriesConfig[] | "error">>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  // Start in the market of whatever is already selected.
  const market = country ?? selection.country;
  const sym = symbolFor(market);
  const cached = seriesByMarket[market];
  const series = Array.isArray(cached) ? cached : null;
  const listError = cached === "error";
  useEffect(() => {
    if (cached !== undefined) return;
    fetch(`${API_BASE}/catalogue/series?country=${market}`).then(res => {
      if (!res.ok) throw new Error(String(res.status));
      return res.json() as Promise<SeriesListResponse>;
    }).then(data => setSeriesByMarket(prev => ({
      ...prev,
      [market]: data.series
    }))).catch(() => setSeriesByMarket(prev_0 => ({
      ...prev_0,
      [market]: "error"
    })));
  }, [market, cached]);
  const results = useMemo(() => {
    if (!series) return [];
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    return series.filter(s => matches(s, tokens));
  }, [series, query]);
  const selectedKeys = useMemo(() => new Set(selection.items.map(itemKey)), [selection.items]);
  const switchingMarket = selection.count > 0 && market !== selection.country;
  const openSeries = (slug: string) => {
    const next = openSlug === slug ? null : slug;
    setOpenSlug(next);
    const cacheKey = `${market}/${slug}`;
    if (!next || Array.isArray(configs[cacheKey])) return;
    fetch(`${API_BASE}/catalogue/series/${market}/${encodeURIComponent(slug)}`).then(res_0 => {
      if (!res_0.ok) throw new Error(String(res_0.status));
      return res_0.json() as Promise<SeriesDetailResponse>;
    }).then(data_0 => setConfigs(prev_1 => ({
      ...prev_1,
      [cacheKey]: data_0.configs
    }))).catch(() => setConfigs(prev_2 => ({
      ...prev_2,
      [cacheKey]: "error"
    })));
  };
  const toggleConfig = async (slug_0: string, config: SeriesConfig) => {
    const key = configKey(slug_0, config.config_number);
    setAddError(null);
    const existing = selection.items.find(it => itemKey(it) === key);
    if (existing) {
      selection.remove(getId(existing));
      return;
    }
    setBusyKey(key);
    try {
      const res_1 = await fetch(`${API_BASE}/recommend/laptop/${market}/${encodeURIComponent(slug_0)}/${config.config_number}`);
      if (!res_1.ok) throw new Error(String(res_1.status));
      selection.toggle((await res_1.json()) as LaptopInformation, market);
    } catch {
      setAddError("That laptop couldn't be loaded. Try adding it again.");
    } finally {
      setBusyKey(null);
    }
  };
  const shown = results.slice(0, RESULT_LIMIT);
  const canCompare = selection.count >= 2;
  return <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
      {/* ── Search & results ─────────────────────────────────────── */}
      <div className="order-2 min-w-0 lg:order-1">
        <div className="flex gap-3">
          <label className="relative flex-1">
            <span className="sr-only">Search laptops</span>
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search a laptop, e.g. MacBook Air 15" className="w-full rounded-full bg-card py-3 pl-11 pr-5 text-sm ring-1 ring-border/70 outline-none transition-shadow placeholder:text-muted-foreground focus:ring-2 focus:ring-brand" />
          </label>
          <div role="radiogroup" aria-label="Market" className="flex shrink-0 rounded-full bg-card p-1 ring-1 ring-border/70">
            {COUNTRIES.map(c => <button key={c.value} type="button" role="radio" aria-checked={market === c.value} onClick={() => {
            setCountry(c.value);
            setOpenSlug(null);
          }} className={cn("cursor-pointer rounded-full px-3.5 text-xs font-semibold transition-colors", market === c.value ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}>
                {c.label}
              </button>)}
          </div>
        </div>

        {switchingMarket && <p className="mt-3 rounded-2xl bg-surface px-4 py-2.5 text-xs leading-relaxed text-muted-foreground ring-1 ring-border/60">
            Prices can&apos;t be compared across currencies, so adding a {market === "us" ? "US" : "UK"} laptop
            will replace the ones you&apos;ve picked so far.
          </p>}
        {addError && <p role="alert" className="mt-3 text-xs font-medium text-destructive">
            {addError}
          </p>}

        <div className="mt-5">
          {listError ? <p className="rounded-3xl bg-card p-8 text-center text-sm text-muted-foreground ring-1 ring-border/60">
              The laptop list didn&apos;t load. Refresh the page to try again.
            </p> : series === null ? <ul className="space-y-3" aria-busy="true">
              {Array.from({
            length: 5
          }).map((_, i) => <li key={i} className="h-[4.5rem] animate-pulse rounded-2xl bg-foreground/5" />)}
            </ul> : shown.length === 0 ? <p className="rounded-3xl bg-card p-8 text-center text-sm text-muted-foreground ring-1 ring-border/60">
              Nothing matches &ldquo;{query}&rdquo;. Try the brand and model, like &ldquo;Dell XPS 14&rdquo;.
            </p> : <>
              <ul className="space-y-3">
                {shown.map(s_0 => {
              const isOpen = openSlug === s_0.slug;
              const list = configs[`${market}/${s_0.slug}`];
              const picked = selection.items.filter(it_0 => toSlug(it_0.laptop.series) === s_0.slug).length;
              const from = formatPrice(sym, s_0.min_price);
              return <li key={s_0.slug} className={cn("overflow-hidden rounded-2xl bg-card ring-1 transition-shadow", isOpen ? "ring-brand/40 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.15)]" : "ring-border/60")}>
                      <button type="button" onClick={() => openSeries(s_0.slug)} aria-expanded={isOpen} className="flex w-full cursor-pointer items-center gap-4 px-4 py-3 text-left sm:px-5">
                        <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-xl bg-surface">
                          {s_0.image ? (/* eslint-disable-next-line @next/next/no-img-element */
                    <img src={s_0.image} alt="" width={48} height={48} className="size-full object-contain" />) : null}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-foreground">{s_0.series}</span>
                          <span className="mt-0.5 block text-xs text-muted-foreground">
                            {s_0.config_count} {s_0.config_count === 1 ? "configuration" : "configurations"}
                            {from && <>, from {from}</>}
                          </span>
                        </span>
                        {picked > 0 && <span className="shrink-0 rounded-full bg-brand/12 px-2 py-0.5 text-[10px] font-semibold text-brand">
                            {picked} added
                          </span>}
                        <ChevronDown className={cn("size-4 shrink-0 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
                      </button>

                      {isOpen && <div className="border-t border-border/60 bg-surface/40">
                          {list === undefined ? <p className="px-5 py-4 text-xs text-muted-foreground">Loading configurations…</p> : list === "error" ? <p className="px-5 py-4 text-xs text-muted-foreground">
                              These configurations didn&apos;t load. Close and reopen to try again.
                            </p> : <ul className="divide-y divide-border/50">
                              {list.map(config_0 => {
                      const key_0 = configKey(s_0.slug, config_0.config_number);
                      return <ConfigRow key={config_0.config_number} config={config_0} sym={sym} selected={selectedKeys.has(key_0) && !switchingMarket} disabled={selection.isFull && !switchingMarket} busy={busyKey === key_0} onAdd={() => toggleConfig(s_0.slug, config_0)} />;
                    })}
                            </ul>}
                        </div>}
                    </li>;
            })}
              </ul>
              {results.length > RESULT_LIMIT && <p className="mt-4 text-center text-xs text-muted-foreground">
                  Showing {RESULT_LIMIT} of {results.length} laptops. Search to find a specific model.
                </p>}
            </>}
        </div>
      </div>

      {/* ── Selection ────────────────────────────────────────────── */}
      <aside className="order-1 rounded-3xl bg-card p-5 ring-1 ring-border/60 shadow-[0_2px_24px_-8px_rgba(0,0,0,0.07)] lg:sticky lg:top-20 lg:order-2">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-foreground">Your laptops</h2>
          <span className="text-xs tabular-nums text-muted-foreground">
            {selection.count} of {selection.max}
          </span>
        </div>

        <ol className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-1">
          {Array.from({
          length: selection.max
        }).map((__0, i_0) => {
          const it_1 = selection.items[i_0];
          if (!it_1) {
            return <li key={`empty-${i_0}`} className={cn(
            // On mobile only the next free slot shows, so the list stays short.
            i_0 === selection.count ? "flex" : "hidden lg:flex", "h-14 items-center justify-center rounded-2xl border border-dashed border-border text-xs text-muted-foreground/70")}>
                  {i_0 === selection.count ? "Add a laptop" : ""}
                </li>;
          }
          const img = getImage(it_1);
          const price = getPrice(it_1);
          return <li key={getId(it_1)} className="flex h-14 items-center gap-2.5 rounded-2xl bg-surface pl-2 pr-1.5 ring-1 ring-border/60">
                <span className="relative grid size-10 shrink-0 place-items-center overflow-hidden rounded-xl bg-card">
                  {img ? (/* eslint-disable-next-line @next/next/no-img-element */
              <img src={img} alt="" width={40} height={40} className="size-full object-contain" />) : null}
                  <span className="absolute inset-x-0 bottom-0 h-1" style={{
                backgroundColor: getCompareColor(i_0)
              }} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-semibold text-foreground">{getDisplayName(it_1)}</span>
                  <span className="block text-[11px] tabular-nums text-muted-foreground">
                    {price > 0 ? `${symbolFor(selection.country)}${price.toLocaleString()}` : "No price yet"}
                  </span>
                </span>
                <button type="button" onClick={() => selection.remove(getId(it_1))} aria-label={`Remove ${getDisplayName(it_1)}`} className="grid size-7 shrink-0 cursor-pointer place-items-center rounded-full text-muted-foreground transition-colors hover:bg-foreground hover:text-background">
                  <X className="size-3.5" />
                </button>
              </li>;
        })}
        </ol>

        <button type="button" onClick={onContinue} disabled={!canCompare} className="mt-4 inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-full bg-brand px-5 text-sm font-semibold text-brand-foreground shadow-[0_4px_18px_-4px_oklch(0.71_0.14_165_/_0.55)] transition-all hover:opacity-90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none">
          {canCompare ? continueLabel : "Add at least 2 to compare"}
        </button>
        {selection.count > 0 && <button type="button" onClick={selection.clear} className="mt-2 w-full cursor-pointer text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
            Clear all
          </button>}
      </aside>
    </div>;
}